import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  CircleAlert,
  ArrowLeft,
  CheckCircle2,
  Database,
  FileJson,
  FileLock2,
  Gift,
  RefreshCw,
  Route,
  ShieldAlert,
  ShieldCheck,
  UploadCloud,
  UserCheck,
  Users,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Brand } from "../../components/Brand";
import { api } from "../../lib/api";
import type {
  AdminOverview,
  ComplimentaryPlanCatalogStatus,
  ComplimentaryPlanImportResult,
} from "../../lib/types";

export const ADMIN_PATH = "/system/rihvo-control";

function dateTime(value: string) {
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function bytes(value: number) {
  if (value < 1024) return `${value} B`;
  if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 ** 3) return `${(value / 1024 ** 2).toFixed(1)} MB`;
  return `${(value / 1024 ** 3).toFixed(1)} GB`;
}

function eventLabel(action: string) {
  return action.split(".").map((word) => word.replaceAll("_", " ")).join(" · ");
}

export function AdminConsoleContent({
  overview,
  refreshing = false,
  onRefresh,
  children,
}: {
  overview: AdminOverview;
  refreshing?: boolean;
  onRefresh?: () => void;
  children?: ReactNode;
}) {
  const { telemetry, security, users } = overview;
  const metrics = [
    { label: "Registered users", value: `${telemetry.registered_users} / ${telemetry.registration_limit}`, note: `${telemetry.registration_slots_remaining} onboarding slots remain`, icon: Users },
    { label: "Active sessions", value: security.sessions.active, note: `${security.sessions.revoked} revoked · ${security.sessions.expired} expired`, icon: Activity },
    { label: "Journeys", value: telemetry.trips, note: `${telemetry.wishlist_items} wishlist ideas`, icon: Route },
    { label: "Private documents", value: telemetry.stored_documents, note: `${bytes(telemetry.stored_bytes)} stored`, icon: FileLock2 },
    { label: "Audit activity", value: telemetry.audit_events_24h, note: "events during the last 24 hours", icon: Database },
  ];

  return <main className="admin-console">
    <header className="admin-header">
      <div>
        <p className="eyebrow">PRIVATE SYSTEM OBSERVATORY</p>
        <h1>Rihvo control room</h1>
        <p>Read-only operational telemetry, registered accounts, and the application security posture.</p>
      </div>
      <div className="admin-header-actions">
        <span className="admin-environment"><span />{overview.environment}</span>
        {onRefresh && <button className="button light" onClick={onRefresh} disabled={refreshing}>
          <RefreshCw size={16} className={refreshing ? "spinning" : ""} />
          {refreshing ? "Refreshing" : "Refresh"}
        </button>}
      </div>
    </header>

    <section className="admin-metrics" aria-label="Application telemetry">
      {metrics.map(({ label, value, note, icon: Icon }) => <article key={label}>
        <span className="admin-metric-icon"><Icon size={19} /></span>
        <div><span>{label}</span><strong>{value}</strong><small>{note}</small></div>
      </article>)}
    </section>

    {children}

    <section className="admin-section">
      <header><div><p className="eyebrow">SECURITY</p><h2>Production posture</h2></div><span>Evaluated {dateTime(overview.generated_at)}</span></header>
      <div className="security-control-grid">
        {security.controls.map((control) => <article key={control.code} className={control.configured ? "secure" : "attention"}>
          {control.configured ? <ShieldCheck size={22} /> : <ShieldAlert size={22} />}
          <div><strong>{control.label}</strong><p>{control.detail}</p></div>
          <span>{control.configured ? "Configured" : "Attention"}</span>
        </article>)}
      </div>
    </section>

    <section className="admin-section">
      <header><div><p className="eyebrow">REGISTERED USERS</p><h2>Onboarding ledger</h2></div><span>{telemetry.active_users} active · {telemetry.verified_users} verified</span></header>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead><tr><th>User</th><th>Joined</th><th>Account</th><th>Verification</th><th>Sessions</th><th>Journeys</th><th>Wishlist</th></tr></thead>
          <tbody>{users.map((user) => <tr key={user.id}>
            <td><strong>{user.full_name}</strong><small>{user.email}</small></td>
            <td>{dateTime(user.created_at)}</td>
            <td><span className={`admin-chip ${user.is_active ? "good" : "bad"}`}>{user.is_active ? "Active" : "Disabled"}</span></td>
            <td><span className={`admin-chip ${user.is_verified ? "good" : "pending"}`}>{user.is_verified ? <><UserCheck size={12} />Verified</> : "Pending"}</span></td>
            <td>{user.active_sessions}</td><td>{user.trip_count}</td><td>{user.wishlist_count}</td>
          </tr>)}</tbody>
        </table>
      </div>
    </section>

    <section className="admin-section">
      <header><div><p className="eyebrow">SECURITY EVENTS</p><h2>Recent authentication and vault activity</h2></div><span>Latest {security.recent_events.length} events</span></header>
      {security.recent_events.length ? <div className="admin-event-list">
        {security.recent_events.map((event) => <article key={event.id}>
          <span className="event-status"><CheckCircle2 size={15} /></span>
          <div><strong>{eventLabel(event.action)}</strong><small>{event.subject_email || "Unknown account"} · {event.entity_type}</small></div>
          <div><span>{event.ip_address || "IP unavailable"}</span><time dateTime={event.created_at}>{dateTime(event.created_at)}</time></div>
        </article>)}
      </div> : <div className="admin-empty"><ShieldCheck size={26} /><div><strong>No security events recorded yet</strong><p>Authentication and private-document activity will appear here.</p></div></div>}
    </section>
  </main>;
}

function ComplimentaryPlanImporter() {
  const queryClient = useQueryClient();
  const [fileName, setFileName] = useState("");
  const [dataset, setDataset] = useState<Record<string, unknown> | null>(null);
  const [fileError, setFileError] = useState("");
  const statusQuery = useQuery({
    queryKey: ["admin", "complimentary-plans"],
    queryFn: () => api<ComplimentaryPlanCatalogStatus>("/admin/complimentary-plans/status"),
  });
  const importPlans = useMutation({
    mutationFn: () => {
      if (!dataset) throw new Error("Choose a vacation dataset first");
      return api<ComplimentaryPlanImportResult>("/admin/complimentary-plans/import", {
        method: "POST",
        body: JSON.stringify(dataset),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "complimentary-plans"] });
      void queryClient.invalidateQueries({ queryKey: ["complimentary-plans"] });
    },
  });

  const inspectFile = async (file?: File) => {
    setDataset(null);
    setFileError("");
    setFileName(file?.name || "");
    importPlans.reset();
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      setFileError("Choose a JSON dataset no larger than 12 MB.");
      return;
    }
    try {
      const value = JSON.parse(await file.text()) as Record<string, unknown>;
      if (!Array.isArray(value.destinations) || value.destinations.length === 0) {
        setFileError("This looks like an import contract. Choose the matching vacation dataset containing a destinations array.");
        return;
      }
      if (value.schema_version !== "1.0.0" || typeof value.dataset_name !== "string") {
        setFileError("The dataset must use schema_version 1.0.0 and include dataset_name.");
        return;
      }
      setDataset(value);
    } catch {
      setFileError("This file is not valid JSON.");
    }
  };

  const destinations = Array.isArray(dataset?.destinations) ? dataset.destinations.length : 0;
  return <section className="admin-section admin-plan-importer">
    <header><div><p className="eyebrow">COMPLIMENTARY CATALOGUE</p><h2>Publish suggested vacation plans</h2></div><span>{statusQuery.data?.active_plans || 0} active suggestions</span></header>
    <div className="admin-import-grid">
      <div className="admin-upload-card">
        <span className="admin-upload-icon"><UploadCloud size={24} /></span>
        <div><strong>Load a validated JSON dataset</strong><p>Upload the India or international vacation dataset. Contracts describe the format but do not contain plans.</p></div>
        <label className="button light" htmlFor="complimentary-plan-file"><FileJson size={16} />Choose JSON</label>
        <input id="complimentary-plan-file" type="file" accept="application/json,.json" onChange={(event) => void inspectFile(event.target.files?.[0])} />
      </div>
      <div className="admin-import-review">
        {dataset ? <>
          <div className="admin-file-ready"><CheckCircle2 size={19} /><div><strong>{String(dataset.dataset_name)}</strong><span>{fileName} · {destinations} destinations · schema {String(dataset.schema_version)}</span></div></div>
          <p>Publishing updates the shared suggestion catalogue. Trips users have already copied remain private and unchanged.</p>
          <button className="button primary" disabled={importPlans.isPending} onClick={() => importPlans.mutate()}><Gift size={16} />{importPlans.isPending ? "Finding destination photos and publishing…" : `Publish ${destinations} plans`}</button>
        </> : <div className="admin-import-placeholder"><Gift size={25} /><strong>Nothing selected</strong><span>Choose one complete dataset to review it before publishing.</span></div>}
        {fileError && <p className="admin-import-error"><CircleAlert size={15} />{fileError}</p>}
        {importPlans.isError && <p className="admin-import-error"><CircleAlert size={15} />{importPlans.error.message}</p>}
        {importPlans.data && <div className="admin-import-success"><CheckCircle2 size={18} /><div><strong>{importPlans.data.dataset_name} published</strong><span>{importPlans.data.templates_created} new · {importPlans.data.templates_updated} updated · {importPlans.data.templates_unchanged} unchanged · {importPlans.data.templates_with_artwork} with destination photos</span></div></div>}
      </div>
    </div>
    {statusQuery.data?.datasets.length ? <div className="admin-dataset-ledger">{statusQuery.data.datasets.map((item) => <article key={`${item.scope}-${item.dataset_name}`}><span>{item.scope}</span><div><strong>{item.dataset_name}</strong><small>{item.plans} suggested plans · Updated {dateTime(item.updated_at)}</small></div></article>)}</div> : null}
  </section>;
}

export function AdminPage() {
  const query = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => api<AdminOverview>("/admin/overview"),
    refetchInterval: 60_000,
    staleTime: 15_000,
  });

  return <div className="admin-page">
    <nav className="admin-topbar" aria-label="Admin console navigation">
      <Brand />
      <Link to="/"><ArrowLeft size={16} />Return to Rihvo.fun</Link>
    </nav>
    {query.isLoading && <div className="admin-loading"><span /><p>Securing the control room…</p></div>}
    {query.isError && <div className="admin-failure"><ShieldAlert size={34} /><h1>Control room unavailable</h1><p>The admin telemetry could not be loaded. No account data was displayed.</p><button className="button primary" onClick={() => void query.refetch()}>Try again</button></div>}
    {query.data && <AdminConsoleContent overview={query.data} refreshing={query.isFetching} onRefresh={() => void query.refetch()}><ComplimentaryPlanImporter /></AdminConsoleContent>}
  </div>;
}
